/**
 * 权限服务 API
 * 获取用户权限资源（菜单、操作权限、超级管理员标识）
 *
 * Requirements: 4.1
 */
import { request } from '@umijs/max';
import type { BackendMenuItem } from '@/utils/menu-builder';

// ============ API 类型定义 ============

/** 操作权限 */
export interface PermissionAction {
  /** 权限分组 */
  group: string;
  /** 权限代码 */
  code: string;
  /** 权限名称 */
  name: string;
  /** 权限描述 */
  description: string;
  /** 排序号 */
  serial_number: number;
  /** 关联菜单 ID */
  menu_id: string;
}

/** 权限资源 API 响应 */
export interface PermissionApiResponse {
  status: string;
  metadata: {
    request_id: string;
    request_elapsed: string;
  };
  data: {
    routes: null;
    actions: PermissionAction[];
    menus: BackendMenuItem[];
    root: boolean;
  };
}

/** 解析后的权限资源数据 */
export interface PermissionResourceData {
  menus: BackendMenuItem[];
  actions: PermissionAction[];
  root: boolean;
}

// ============ API 函数 ============

/**
 * 获取权限资源列表
 * GET /api/v1/authz/me/permission/resources
 *
 * 返回当前用户的菜单列表、操作权限列表和超级管理员标识
 * 需要 Bearer Token 认证（由请求拦截器自动注入）
 *
 * @returns 解析后的权限资源数据（menus, actions, root）
 */
export async function fetchPermissionResources(
  options?: { [key: string]: any },
): Promise<PermissionResourceData> {
  const response = await request<PermissionApiResponse>(
    '/api/v1/authz/me/permission/resources',
    {
      method: 'GET',
      ...(options || {}),
    },
  );

  const { menus = [], actions = [], root = false } = response?.data ?? {};

  return {
    menus,
    actions,
    root,
  };
}
